import type { FileEntry, Group } from "../state/types";
import type {
  UploadGroupResult,
  UploadStatus,
  UploaderDeps,
} from "./types";
import { uploadOneGroup } from "./uploadOneGroup";

const DEFAULT_CONCURRENCY = 2;

export type RunAllUploadsArgs = {
  /**
   * Groups to upload, in order. Caller is expected to filter this to
   * the groups that actually need work (e.g. only the failed groups
   * during a retry).
   */
  groups: Group[];
  files: Record<string, FileEntry>;
  period_date: string;
  deps: UploaderDeps;
  /**
   * Fires on every per-group status transition. Called from inside the
   * promise pool; the callback should be cheap and side-effect-safe.
   */
  onGroupStatus: (groupId: string, status: UploadStatus) => void;
  /**
   * Max concurrent groups. Defaults to 2 per spec. Inside one group,
   * file uploads are sequential — that lives in `uploadOneGroup`.
   */
  concurrency?: number;
};

/**
 * Run `uploadOneGroup` for every group with a bounded concurrency pool.
 *
 * Semantics:
 *   - At most `concurrency` (default 2) groups run at the same time.
 *   - Each group's failure is isolated — `Promise.allSettled`-style.
 *     One group failing never cancels another.
 *   - This function only completes after every group has reached a
 *     terminal state (`complete` or `failed`).
 *   - Never throws. Defensive `try/catch` around each `uploadOneGroup`
 *     call so an unexpected throw from I/O can't kill the pool.
 *
 * The returned map is keyed by group id; insertion order mirrors the
 * order groups completed (not the input order).
 */
export async function runAllUploads(
  args: RunAllUploadsArgs,
): Promise<Map<string, UploadGroupResult>> {
  const results = new Map<string, UploadGroupResult>();
  if (args.groups.length === 0) return results;

  const concurrency = Math.max(
    1,
    args.concurrency ?? DEFAULT_CONCURRENCY,
  );

  // Shared cursor across workers. Each worker grabs the next index and
  // processes that group until the input list is exhausted.
  let cursor = 0;

  async function worker(): Promise<void> {
    while (true) {
      const myIndex = cursor;
      cursor += 1;
      if (myIndex >= args.groups.length) return;
      const group = args.groups[myIndex];
      let groupResult: UploadGroupResult;
      try {
        groupResult = await uploadOneGroup(
          {
            group,
            files: args.files,
            period_date: args.period_date,
          },
          args.deps,
          (status: UploadStatus) => args.onGroupStatus(group.id, status),
        );
      } catch (err: unknown) {
        // Defense in depth — uploadOneGroup is designed to never
        // throw, but an unexpected runtime error (e.g. dependency
        // misuse) must not kill the pool.
        const message =
          err instanceof Error
            ? `Unexpected error: ${err.message}`
            : "Unexpected error during upload";
        args.onGroupStatus(group.id, { kind: "failed", message });
        groupResult = { kind: "failed", message };
      }
      results.set(group.id, groupResult);
    }
  }

  const workerCount = Math.min(concurrency, args.groups.length);
  const workers: Promise<void>[] = [];
  for (let i = 0; i < workerCount; i += 1) workers.push(worker());
  await Promise.all(workers);

  return results;
}
